
// 1. Base Class for all Application Errors
export class JosephineError extends Error {
    // Allows tracking the original HTTP status code if applicable
    public readonly statusCode: number; 
    
    // Allows tracking the original Supabase error code for diagnostics
    public readonly originalCode: string | null;

    constructor(
        message: string, 
        statusCode: number = 500, 
        originalCode: string | null = null
    ) {
        super(message);
        // Set the name to the class name for easier debugging/logging
        this.name = this.constructor.name; 
        this.statusCode = statusCode;
        this.originalCode = originalCode;
        // Proper setup for extending Error in TypeScript/Node
        Object.setPrototypeOf(this, JosephineError.prototype); 
    }
}

// 2. Client-Actionable Errors (User-facing issues)
export class ResourceNotFoundError extends JosephineError {
    constructor(message: string = "The requested item was not found.") {
        super(message, 404);
        Object.setPrototypeOf(this, ResourceNotFoundError.prototype);
    }
}

export class PermissionDeniedError extends JosephineError {
    constructor(message: string = "Access denied due to insufficient permissions.", originalCode: string | null = null) {
        super(message, 403, originalCode);
        Object.setPrototypeOf(this, PermissionDeniedError.prototype);
    }
}

export class ValidationError extends JosephineError {
    // Optionally include a map of field-specific errors
    constructor(message: string = "Input validation failed.", originalCode: string | null = null) {
        super(message, 400, originalCode);
        Object.setPrototypeOf(this, ValidationError.prototype);
    }
}

// 3. Infrastructure/System Errors
export class NetworkError extends JosephineError {
    constructor(message: string = "Could not connect to the server. Check your connection.", originalCode: string | null = null) {
        super(message, 503, originalCode); // 503 Service Unavailable
        Object.setPrototypeOf(this, NetworkError.prototype);
    }
}

// 4. Fallback Error (Should capture unexpected problems)
export class UnknownApplicationError extends JosephineError {
    constructor(message: string = "An unexpected error occurred on the server.", originalCode: string | null = null) {
        super(message, 500, originalCode); 
        Object.setPrototypeOf(this, UnknownApplicationError.prototype);
    }
}